import { z } from "zod";

/**
 * Sözleşme YAŞAM DÖNGÜSÜ durum makinesi (CLOS-114 `[076]`).
 *
 * ### ⚠ Matris KONFİGÜRASYONDA, koda gömülü değil
 *
 * Kart on durumu ve kanonik geçiş kurallarını tarif ediyor ama 10×10'luk matrisi tek tek
 * yazmıyor. Matris kartın KENDİ KURALLARINDAN türetildi ve `config/contractLifecycle.json`
 * içine kondu; koda gömseydik bir geçiş kuralını değiştirmek deploy gerektirir ve kuralın
 * ne olduğu ancak kod okunarak öğrenilirdi.
 *
 * ### ⚠ Kaynak, geçişin parçası
 *
 * Kart: *"workflow tarafından yönetilen duruma DOĞRUDAN KULLANICI GEÇİŞİ YAPILAMAZ."* Bu
 * yüzden her geçiş hangi kaynaklardan yapılabileceğini söylüyor. Yalnız hedef durumun
 * "workflow'a ait" olmasına baksaydık, workflow'un kendi kolları arasında geri dönmesi
 * (onaydan müzakereye) ifade edilemezdi.
 */

export const CONTRACT_TRANSITION_SOURCES = ["user", "workflow", "schedule"] as const;
export type ContractTransitionSource = (typeof CONTRACT_TRANSITION_SOURCES)[number];

export const contractLifecycleConfigSchema = z.object({
  states: z
    .array(
      z.object({
        code: z.string().min(1),
        label: z.string().min(1),
        /** Bu duruma yalnız workflow sokabilir; kullanıcıya sunulmaz. */
        workflowManaged: z.boolean(),
        /** Bu durumdaki sözleşme aktif kapasite tutuyor mu (CLOS-198). */
        holdsCapacity: z.boolean(),
        /**
         * Bu durumda sözleşme METNİ düzenlenebilir mi (CLOS-118 `[086]`).
         *
         * ⚠ Karar KATALOGDA, kodda değil: kart *"parametreler hard-code edilmez"* diyor.
         * Koda gömseydik yeni bir durumun metni dondurup dondurmadığı sürüm çıkmadan
         * değiştirilemezdi. Kartın kuralı: *"onaylı, mutabık, imzalı veya başka biçimde
         * değişmez hâle gelmiş versiyonlar editöre SALT OKUNUR açılır."*
         */
        contentEditable: z.boolean(),
      }),
    )
    .min(1),
  transitions: z
    .array(
      z.object({
        from: z.string().min(1),
        to: z.string().min(1),
        sources: z.array(z.enum(CONTRACT_TRANSITION_SOURCES)).min(1),
        /** Geçişi yapmaya yeten yetki — kapı geçiş başına, tek bir uç yetkisi değil. */
        permission: z.string().min(1),
      }),
    )
    .min(1),
  archiveState: z.string().min(1),
  restorePermission: z.string().min(1),
});
export type ContractLifecycleConfig = z.infer<typeof contractLifecycleConfigSchema>;
export type ContractStateOption = ContractLifecycleConfig["states"][number];
export type ContractTransitionRule = ContractLifecycleConfig["transitions"][number];

export function findTransition(
  config: ContractLifecycleConfig,
  from: string,
  to: string,
): ContractTransitionRule | undefined {
  return config.transitions.find((t) => t.from === from && t.to === to);
}

/**
 * Kullanıcıya SUNULABİLECEK geçişler.
 *
 * ⚠ Kart: *"desteklenmeyen workflow geçişi KULLANICIYA SUNULMAZ"* ve *"workflow kaynaklı
 * durumlar kullanıcı tarafından YANILTICI BİÇİMDE ATLANAMAZ."* Süzme sunucuda, çünkü
 * arayüz kendi listesini tutsaydı bir sürüm farkı, kullanıcıya sunucunun reddedeceği bir
 * düğme gösterirdi — ya da daha kötüsü, kabul edeceği ama sunulmaması gereken birini.
 */
export function userTransitionsFrom(
  config: ContractLifecycleConfig,
  from: string,
): ContractTransitionRule[] {
  return config.transitions.filter((t) => t.from === from && t.sources.includes("user"));
}

export const contractTransitionInputSchema = z.object({
  to: z.string().min(1, "Hedef durum zorunludur"),
  /** Serbest metin gerekçe — kart: *"geçiş nedeni/kaynağı"* kaydedilir. */
  reason: z.string().max(500, "Gerekçe en fazla 500 karakter olabilir").nullish(),
  /**
   * UYARI ONAYI (CLOS-122 `[104]`).
   *
   * ⚠ Kart: *"yalnız uyarı gerektiriyorsa etki açıkça gösterilir ve yetkili kullanıcı
   * ONAYI alınır."* Onay isteğin İÇİNDE taşınıyor, sunucuda bir oturum bayrağında değil:
   * "kullanıcı bir yerlerde onaylamıştı" durumu, arşivlemeyi ekranda hiç görülmemiş bir
   * uyarıyla yapılabilir kılardı.
   */
  acknowledgeWarnings: z.boolean().default(false),
});
export type ContractTransitionInput = z.infer<typeof contractTransitionInputSchema>;

export type ContractStatusTransition = {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  source: ContractTransitionSource;
  reason: string | null;
  actorId: string | null;
  workflowId: string | null;
  versionId: string | null;
  createdAt: string;
};

/**
 * ARŞİVLEME ÖN İZLEMESİ (CLOS-122 `[104]`).
 *
 * Kullanıcıya *"ne olacak"* sorusunun cevabını arşivlemeden ÖNCE veriyor: açık işler,
 * engel olup olmadığı ve arşivin etkileri. Etkiler burada, ekranın uydurduğu cümleler
 * olarak değil şemada duruyor (refleks 4).
 */
export type ContractArchivePreview = {
  /** Arşivleme şu an mümkün mü — engelleyici açık iş varsa hayır. */
  canArchive: boolean;
  /** Engelleyici açık işler; varsa arşivleme atomik olarak reddedilir. */
  blocking: { provider: string; message: string; referenceId?: string }[];
  /** Uyarılar; kullanıcı açıkça onaylarsa arşivleme yapılır. */
  warnings: { provider: string; message: string; referenceId?: string }[];
  /** Arşivlemenin sonuçları — kullanıcı ne kaybettiğini ve neyi kaybetmediğini bilmeli. */
  effects: string[];
};

/** Arşivin ne yaptığı ve YAPMADIĞI — kart kriterlerinin birebir karşılığı. */
export const CONTRACT_ARCHIVE_EFFECTS = [
  "Sözleşme aktif listelerden çıkar; arşiv filtresiyle görünmeye devam eder.",
  "Aktif sözleşme kapasitesinden düşer.",
  "Belgeler, ekler, versiyonlar, yorumlar ve geçmiş SİLİNMEZ.",
  "Arşivlenen sözleşme depolama alanını kullanmaya devam eder.",
  "Arşivden geri alma, arşivlenmeden önceki duruma döner ve kapasite yeniden gerekir.",
] as const;

/**
 * ARŞİVDEN GERİ ALMA ÖN İZLEMESİ (CLOS-123 `[105]`).
 *
 * ### ⚠ Hedef durum GEÇMİŞTEN geliyor
 *
 * Arşivlenmeden önceki durum, uydurulmayan tek cevap. Sabit bir hedef seçseydik aktif bir
 * sözleşmeyi arşivleyip geri almak onu sessizce taslağa düşürür ve hukuki durumunu
 * değiştirirdi.
 *
 * ### ⚠ Workflow durumu SESSİZCE canlanmıyor
 *
 * Kart: *"eski açık iş veya geçersiz workflow durumu SESSİZCE canlandırılmaz."* Sözleşme
 * inceleme/onay/imza gibi bir workflow durumundayken arşivlendiyse, geri alma o durumu
 * yazabilir ama arkasındaki süreci geri getiremez — kullanıcı bunu bilerek onaylıyor.
 */
export type ContractRestorePreview = {
  canRestore: boolean;
  /** Arşivlenmeden önceki durum; geçmiş yoksa `null`. */
  targetStatus: string | null;
  targetLabel: string | null;
  /** Hedef durum bir workflow tarafından yönetiliyor mu. */
  targetWorkflowManaged: boolean;
  blocking: string[];
  warnings: string[];
  effects: string[];
};

/** Geri almanın sonuçları — kullanıcı ne olacağını görmeden onaylamamalı. */
export const CONTRACT_RESTORE_EFFECTS = [
  "Sözleşme arşivden çıkar ve aktif listelerde yeniden görünür.",
  "Aktif sözleşme kapasitesinden bir yer tutar.",
  "Aynı sözleşme kimliği korunur; yeni bir kayıt oluşturulmaz.",
  "Belgeler, ekler, versiyonlar ve geçmiş olduğu gibi kalır.",
] as const;

/** Geri alma engelleri — kapalı küme; ekran metni buradan okunuyor (refleks 4). */
export const CONTRACT_RESTORE_BLOCK_NAMES = {
  not_archived: "Sözleşme arşivde değil.",
  no_history: "Arşivlenmeden önceki durum bulunamadı; geri alınamaz.",
  no_capacity: "Aktif sözleşme kapasiteniz dolu; önce yer açmanız gerekiyor.",
  no_license: "Aboneliğiniz şu anda yeni aktif sözleşmeye izin vermiyor.",
} as const;
export type ContractRestoreBlock = keyof typeof CONTRACT_RESTORE_BLOCK_NAMES;

/** Workflow durumuna dönüşün uyarısı — sessiz canlandırma yasağının kullanıcı yüzü. */
export const CONTRACT_RESTORE_WORKFLOW_WARNING =
  "Bu sözleşme bir süreç durumundayken arşivlenmiş. Geri alındığında durum yeniden yazılır ama süreç kendiliğinden devam etmez; ilgili adımı yeniden başlatmanız gerekir.";

export const contractRestoreInputSchema = z.object({
  reason: z.string().max(500, "Gerekçe en fazla 500 karakter olabilir").nullish(),
  /** Workflow durumuna dönüş uyarısı okundu mu (`[105]`). */
  acknowledgeWarnings: z.boolean().default(false),
});
export type ContractRestoreInput = z.infer<typeof contractRestoreInputSchema>;

export type ContractLifecycleView = {
  status: string;
  states: ContractStateOption[];
  /** Bu kullanıcının ŞU AN yapabileceği geçişler; workflow kolları burada asla yok. */
  availableTransitions: { to: string; label: string; permission: string }[];
  /** Arşivden dönüş mümkün mü ve nereye — hedef GEÇMİŞTEN gelir, sabit değil. */
  restoreTo: string | null;
  history: ContractStatusTransition[];
};
